import { Link } from "wouter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/status-badge";
import { useCrmText } from "@/lib/crm/i18n";
import type { CrmEntity } from "@/lib/crm/fields";
import { useCrmFiles } from "@/lib/crm/api";
import { formatDateTime } from "@/lib/crm/view-engine";
import { FileText, Loader2, RefreshCw, Send } from "lucide-react";

/** Files tab: documents sent from the record or to its e-mail. */
export function CrmFiles({ entity, recordId, onSendDocument, canSend }: { entity: CrmEntity; recordId: string; onSendDocument: () => void; canSend: boolean }) {
  const { lang, tx } = useCrmText();
  const { data, isLoading, isFetching, refetch } = useCrmFiles(entity, recordId);

  return (
    <div className="space-y-3" data-testid="record-files">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> {tx("refresh")}
        </Button>
        {canSend && (
          <Button size="sm" onClick={onSendDocument} data-testid="files-send-document">
            <Send className="h-4 w-4" /> {tx("sendDocument")}
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !data || data.length === 0 ? (
        <p className="rounded-lg border py-10 text-center text-sm text-muted-foreground">{tx("noFiles")}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tx("document")}</TableHead>
                <TableHead>{tx("status")}</TableHead>
                <TableHead className="whitespace-nowrap">{tx("sentOn")}</TableHead>
                <TableHead className="whitespace-nowrap">{tx("sentBy")}</TableHead>
                <TableHead>{tx("signers")}</TableHead>
                <TableHead>{tx("source")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((f) => (
                <TableRow key={f.documentId} data-testid="file-row">
                  <TableCell className="min-w-[12rem]">
                    <Link href={`/documents/${f.documentId}`} className="inline-flex items-center gap-2 font-medium underline-offset-2 hover:underline">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="break-all">{f.name}</span>
                    </Link>
                  </TableCell>
                  <TableCell><StatusBadge status={f.status} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{formatDateTime(f.createdAt, lang)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{f.sentByName || "—"}</TableCell>
                  <TableCell className="text-xs">{f.signers.join(", ") || "—"}</TableCell>
                  <TableCell>
                    {f.linked ? (
                      <Badge variant="outline" className="font-normal">{tx("linkedSource")}</Badge>
                    ) : f.viaLabel && f.viaId ? (
                      <Link href={`/${f.viaEntity === "lead" ? "leads" : "contacts"}/${f.viaId}`}>
                        <Badge variant="outline" className="font-normal hover-elevate">
                          {tx(f.viaEntity === "lead" ? "viaLead" : "viaClient", { name: f.viaLabel })}
                        </Badge>
                      </Link>
                    ) : (
                      <Badge variant="outline" className="font-normal">{tx("emailSource")}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
